// Phase 4 · M2 · Pickup Authorization client library.
//
// Per-child list of trusted adults the family pre-authorises to pick
// the child up. Distinct from `family_members` (legal guardians).
//
// Photo paths live in the existing `child-photos` bucket under
// `pickup/{child_id}/{ts}.jpg` so the same three-role storage policies
// apply (parent/teacher/admin scoped read; parent/admin write).

import { supabase, isSupabaseConfigured } from "@/lib/supabase";

const BUCKET = "child-photos";
const SIGNED_URL_TTL = 60 * 60; // 1 hour
const MAX_DIM = 512;
const MAX_BYTES = 4 * 1024 * 1024;

const SELECT = `
  id, organization_id, child_id, family_id,
  full_name, relationship, phone, photo_path, pin,
  is_emergency_contact, notes, active,
  added_by, created_at, updated_at
`;

/** Parent/admin — list authorisations for a child (newest first). */
export async function listForChild(childId, { includeInactive = false } = {}) {
  if (!isSupabaseConfigured || !childId) return [];
  let q = supabase
    .from("pickup_authorizations")
    .select(SELECT)
    .eq("child_id", childId)
    .order("is_emergency_contact", { ascending: false })
    .order("created_at", { ascending: false });
  if (!includeInactive) q = q.eq("active", true);
  const { data, error } = await q;
  if (error) {
    if (error.code === "42P01") return []; // pre-migration
    throw error;
  }
  return data || [];
}

/** Parent — list all authorisations across every child in the family. */
export async function listForFamily(familyId) {
  if (!isSupabaseConfigured || !familyId) return [];
  const { data, error } = await supabase
    .from("pickup_authorizations")
    .select(`${SELECT}, child:children(id, first_name, last_name)`)
    .eq("family_id", familyId)
    .eq("active", true)
    .order("created_at", { ascending: false });
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function createAuthorization({
  organizationId,
  childId,
  familyId,
  fullName,
  relationship,
  phone,
  photoPath,
  pin,
  isEmergencyContact,
  notes,
  addedBy,
}) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  if (!fullName?.trim()) throw new Error("Full name is required");
  if (pin && !/^\d{4}$/.test(pin)) throw new Error("PIN must be 4 digits");
  const { data, error } = await supabase
    .from("pickup_authorizations")
    .insert({
      organization_id: organizationId,
      child_id: childId,
      family_id: familyId,
      full_name: fullName.trim(),
      relationship: relationship?.trim() || null,
      phone: phone?.trim() || null,
      photo_path: photoPath || null,
      pin: pin || null,
      is_emergency_contact: !!isEmergencyContact,
      notes: notes?.trim() || null,
      added_by: addedBy,
    })
    .select(SELECT)
    .single();
  if (error) throw error;
  return data;
}

export async function updateAuthorization(id, patch) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  if (patch.pin && !/^\d{4}$/.test(patch.pin)) {
    throw new Error("PIN must be 4 digits");
  }
  const { data, error } = await supabase
    .from("pickup_authorizations")
    .update(patch)
    .eq("id", id)
    .select(SELECT)
    .single();
  if (error) throw error;
  return data;
}

/** Soft-delete: flip active=false. Keeps history for audit. */
export async function deactivate(id) {
  return updateAuthorization(id, { active: false });
}

/** Hard delete (admin or parent if needed). */
export async function removeAuthorization(id) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { error } = await supabase
    .from("pickup_authorizations")
    .delete()
    .eq("id", id);
  if (error) throw error;
}

// ---------- photo handling ----------

async function shrink(file) {
  if (!file.type.startsWith("image/")) {
    throw new Error("Photo must be an image");
  }
  if (file.size > MAX_BYTES) {
    throw new Error("Image too large (max 4 MB before resize)");
  }
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Could not read image"));
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("Could not decode image"));
    i.src = dataUrl;
  });
  const ratio = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  return new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.85),
  );
}

export async function uploadPickupPhoto({ childId, file, oldPath }) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  if (!file) throw new Error("No file provided");
  const blob = await shrink(file);
  const path = `pickup/${childId}/${Date.now()}.jpg`;
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, { contentType: "image/jpeg", upsert: false });
  if (upErr) throw upErr;
  if (oldPath && oldPath !== path) {
    await supabase.storage.from(BUCKET).remove([oldPath]).catch(() => {});
  }
  return path;
}

const urlCache = new Map();
export async function getPickupPhotoUrl(path) {
  if (!path || !isSupabaseConfigured) return null;
  const now = Date.now();
  const cached = urlCache.get(path);
  if (cached && cached.expiresAt > now) return cached.url;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL);
  if (error) {
    urlCache.delete(path);
    return null;
  }
  urlCache.set(path, {
    url: data.signedUrl,
    expiresAt: now + (SIGNED_URL_TTL - 60) * 1000,
  });
  return data.signedUrl;
}
